import { Code, ConnectError, createClient, type Client, type Interceptor, type Transport } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { AuthService } from '@/gen/tagn/v1/auth_pb';
import { NoteService } from '@/gen/tagn/v1/notes_pb';
import type { Session, SessionStore } from './session';

export interface Api {
  auth: Client<typeof AuthService>;
  notes: Client<typeof NoteService>;
}

const PUBLIC_METHODS = new Set(['GetInvitation', 'Register', 'Login', 'Refresh', 'GetSharedNote']);
const REFRESH_MARGIN_MS = 30_000;

/** Creates an interceptor that attaches access tokens and transparently refreshes them. */
export function authInterceptor(store: SessionStore, refreshClient: () => Client<typeof AuthService>): Interceptor {
  let inflight: Promise<Session> | null = null;

  const refresh = (): Promise<Session> => {
    inflight ??= (async () => {
      const current = store.get();
      if (!current) throw new ConnectError('not signed in', Code.Unauthenticated);
      try {
        const res = await refreshClient().refresh({ refreshToken: current.refreshToken });
        const next = res.tokens ? store.setTokens(res.tokens) : null;
        if (!next) throw new ConnectError('refresh failed', Code.Unauthenticated);
        return next;
      } catch (err) {
        if (ConnectError.from(err).code === Code.Unauthenticated) store.set(null);
        throw err;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  };

  return (next) => async (req) => {
    if (PUBLIC_METHODS.has(req.method.name)) return next(req);
    let session = store.get();
    if (!session) throw new ConnectError('not signed in', Code.Unauthenticated);
    if (session.accessExpiresAt - REFRESH_MARGIN_MS < Date.now()) session = await refresh();
    req.header.set('Authorization', `Bearer ${session.accessToken}`);
    try {
      return await next(req);
    } catch (err) {
      if (ConnectError.from(err).code !== Code.Unauthenticated) throw err;
      session = await refresh();
      req.header.set('Authorization', `Bearer ${session.accessToken}`);
      return next(req);
    }
  };
}

/** Builds the API clients for a transport factory (injectable for tests). */
export function createApi(store: SessionStore, makeTransport: (interceptors: Interceptor[]) => Transport): Api {
  const plain = makeTransport([]);
  const refreshClient = createClient(AuthService, plain);
  const transport = makeTransport([authInterceptor(store, () => refreshClient)]);
  return { auth: createClient(AuthService, transport), notes: createClient(NoteService, transport) };
}

/**
 * Base URL of the API: VITE_API_URL at build time (e.g. https://api.tag-n.message.red),
 * otherwise the page origin (same-origin setup, dev proxy).
 */
export function apiBaseUrl(configured: string | undefined, origin: string): string {
  const url = configured?.trim().replace(/\/+$/, '');
  return url !== undefined && url !== '' ? url : origin;
}

/** Browser transport (Connect protocol, binary). */
export function browserTransport(baseUrl: string) {
  return (interceptors: Interceptor[]) => createConnectTransport({ baseUrl, useBinaryFormat: true, interceptors });
}

/** Human readable German error message. */
export function errorMessage(err: unknown): string {
  const e = ConnectError.from(err);
  switch (e.code) {
    case Code.Unavailable:
    case Code.Unknown:
      return 'Server nicht erreichbar. Bist du online?';
    case Code.Unauthenticated:
      return 'Anmeldung fehlgeschlagen oder abgelaufen.';
    case Code.PermissionDenied:
      return 'Dafür fehlen dir die Berechtigungen.';
    case Code.NotFound:
      return 'Nicht gefunden.';
    case Code.AlreadyExists:
      return 'Existiert bereits.';
    case Code.ResourceExhausted:
      return 'Zu viele Versuche. Bitte warte einen Moment.';
    case Code.FailedPrecondition:
      return 'Diese Einladung ist abgelaufen oder wurde bereits verwendet.';
    case Code.InvalidArgument:
      return invalidArgument(e.rawMessage);
    default:
      return 'Etwas ist schiefgelaufen.';
  }
}

function invalidArgument(msg: string): string {
  if (msg.includes('password')) return 'Das Passwort muss mindestens 10 Zeichen lang sein.';
  if (msg.includes('email')) return 'Bitte gib eine gültige E-Mail-Adresse ein.';
  if (msg.includes('display name')) return 'Bitte gib einen Namen ein.';
  if (msg.includes('yourself')) return 'Du kannst nicht mit dir selbst teilen.';
  return 'Ungültige Eingabe.';
}
